import React from 'react';
import Loading from 'src/components/Loading';
import { filterDefault } from '../../../types/typesVPO';

interface TableRegionReportProps {
  FactoryVPO: filterDefault[];
  FilterColumns: filterDefault[];
  currentGroup?: string;
  reportsData?: any;
  reportsLoading?: boolean;
}

export const TableRegionReport: React.FC<TableRegionReportProps> = ({ FactoryVPO, FilterColumns, currentGroup, reportsData, reportsLoading }) => {
  const months = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];

  const getCellColor = (value: number | null) => {
    if (value === null) return 'bg-gray-200';
    if (value >= 0 && value < 50) return 'bg-[#FDD8D8]';
    if (value >= 50 && value < 60) return 'bg-[#FDD8D8]';
    if (value >= 60 && value < 70) return 'bg-[#FEF3C7]';
    if (value >= 70) return 'bg-[#C2F3D6]';
    return 'bg-[#E5E7EB]';
  };

  const getPercentage = (factoryId: string | number, columnId: string | number, monthIndex: number): number | null => {
    if (!reportsData?.data?.[factoryId]) return null;
    const monthData = reportsData.data[factoryId][monthIndex + 1];
    if (!monthData?.directions?.[columnId]) return null;
    const directionData = monthData.directions[columnId];
    if (directionData.totalValid === 0) return null;
    return directionData.percentage;
  };

  const getAveragePercentage = (factoryId: string | number, monthIndex: number): number | null => {
    if (!reportsData?.data?.[factoryId]) return null;
    const monthData = reportsData.data[factoryId][monthIndex + 1];
    if (!monthData) return null;
    if (monthData.totalValid === 0) return null;
    return monthData.averagePercentage;
  };

  const getGroupAveragePercentage = (monthIndex: number): number | null => {
    if (!reportsData?.data || !FactoryVPO || FactoryVPO.length === 0) return null;
    let totalYes = 0;
    let totalValid = 0;
    FactoryVPO.forEach((factory) => {
      const factoryId = factory.ID;
      const monthData = reportsData.data[factoryId]?.[monthIndex + 1];
      if (monthData) {
        totalYes += monthData.totalYes || 0;
        totalValid += monthData.totalValid || 0;
      }
    });
    return totalValid > 0 ? Math.round((totalYes * 100) / totalValid) : null;
  };

  const getGroupColumnPercentage = (columnId: string | number, monthIndex: number): number | null => {
    if (!reportsData?.data || !FactoryVPO || FactoryVPO.length === 0) return null;
    let totalYes = 0;
    let totalValid = 0;
    FactoryVPO.forEach((factory) => {
      const factoryId = factory.ID;
      const monthData = reportsData.data[factoryId]?.[monthIndex + 1];
      if (monthData?.directions?.[columnId]) {
        totalYes += monthData.directions[columnId].yesCount || 0;
        totalValid += monthData.directions[columnId].totalValid || 0;
      }
    });
    return totalValid > 0 ? Math.round((totalYes * 100) / totalValid) : null;
  };

  const renderTable = (title: string, factoryId: string | number | null = null, isGroup: boolean = false) => {
    const now = new Date();
    const currentMonth = now.getMonth(); // 0–11
    const currentYear = now.getFullYear();
    const reportYear = reportsData?.year || currentYear;

    return (
      <div className='mb-8'>
        {reportsLoading && (
          <div className='h-[600px] flex items-center justify-center rounded-lg shadow'>
            <Loading />
          </div>
        )}
        {!reportsLoading && (
          <>
            <h3 className={`mb-4 text-xl font-semibold text-black`}>{title}</h3>
            <div className='overflow-x-auto'>
              <table className='w-full border-separate border-spacing-0 table-fixed'>
                <thead>
                  <tr className='bg-gray-50'>
                    <th className='w-14 p-2 text-xs text-black font-normal border-b border-r-2 border-gray-300'>Колонна</th>
                    {months.map((month, i) => (
                      <th key={i} className='w-20 border-b border-gray-400 p-2 text-center text-xs font-normal text-black border-r'>
                        {month}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {FilterColumns?.map((column) => (
                    <tr key={column.ID} className='text-sm text-black bg-gray-50'>
                      <td className='p-2 text-black border-b border-r-2 border-gray-300'>{column.Code || column.Title}</td>
                      {months.map((_, monthIndex) => {
                        const percentage = isGroup ? getGroupColumnPercentage(column.ID, monthIndex) : getPercentage(factoryId!, column.ID, monthIndex);
                        const isEmpty = percentage === null || percentage === undefined || percentage === 0;
                        return (
                          <td key={monthIndex} className={`p-2 text-center text-sm border-b border-gray-400 border-r ${isEmpty ? 'bg-gray-200' : getCellColor(percentage)}`}>
                            {isEmpty ? '-' : `${percentage}%`}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className='text-black bg-gray-50'>
                    <td className='p-2 text-xs font-medium border-b border-r-2 border-gray-300'>Всего средняя</td>
                    {months.map((_, monthIndex) => {
                      const totalAverage = isGroup ? getGroupAveragePercentage(monthIndex) : getAveragePercentage(factoryId!, monthIndex);
                      const isEmpty = totalAverage === null || totalAverage === undefined || totalAverage === 0;
                      return (
                        <td key={monthIndex} className={`p-2 text-center text-sm font-medium border-b border-gray-400 border-r ${isEmpty ? 'bg-gray-200' : getCellColor(totalAverage)}`}>
                          {isEmpty ? '-' : `${totalAverage}%`}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      {renderTable(currentGroup || '', null, true)}
      {FactoryVPO?.map((factory) => (
        <div key={factory.ID}>{renderTable(factory.Title, factory.ID, false)}</div>
      ))}
    </div>
  );
};
